import {
  BUDGET_MONTH_LABELS,
  BUDGET_NATURE_LABELS,
  type ActualVsBudget,
  type ActualVsBudgetRow,
  type BudgetReportGroupBy,
  budgetReportGroupBySchema,
} from "@station/contracts";
import { Alert, Button, Card, EmptyState, FormField, Select, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Download, Target } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { Amount, todayIstanbul, useCompanyStations } from "../../../features/accounting/shared.js";
import { useActualVsBudget, useBudgets } from "../../../features/budget/queries.js";
import { percentText, useBudgetCrumb, VarianceBadge } from "../../../features/budget/shared.js";
import { ExpensesPage, Section } from "../../../features/expenses/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const searchSchema = z.object({
  ...companySearchShape,
  budgetId: z.string().uuid().optional().catch(undefined),
  throughMonth: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
  stationId: z.string().uuid().optional().catch(undefined),
  group: budgetReportGroupBySchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/butce/rapor")({
  validateSearch: (search) => searchSchema.parse(search),
  component: BudgetReportPage,
});

function BudgetReportPage(): ReactElement {
  const { t } = useTranslation();
  const crumb = useBudgetCrumb();
  return (
    <ExpensesPage
      title={t("budget.report.title")}
      description={t("budget.report.description")}
      breadcrumb={[crumb, { label: t("budget.report.title") }]}
    >
      {({ companyId }) => <ReportBody companyId={companyId} />}
    </ExpensesPage>
  );
}

function ReportBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const shape = useTenantShape();
  const stations = useCompanyStations(companyId);
  const budgets = useBudgets(companyId);

  // Varsayılan bütçe: bu yılın ETKİN bütçesi; yoksa listedeki ilk bütçe.
  const currentYear = Number(todayIstanbul().slice(0, 4));
  const fallbackBudget =
    (budgets.data ?? []).find((item) => item.status === "ACTIVE" && item.fiscalYear === currentYear) ??
    (budgets.data ?? []).find((item) => item.status === "ACTIVE") ??
    (budgets.data ?? [])[0] ??
    null;
  const budgetId = search.budgetId ?? fallbackBudget?.id ?? null;
  const throughMonth = search.throughMonth ?? Number(todayIstanbul().slice(5, 7));
  const groupBy = search.group ?? "ACCOUNT";

  const report = useActualVsBudget(companyId, {
    budgetId,
    throughMonth,
    ...(search.stationId ? { stationId: search.stationId } : {}),
    groupBy,
    includeMonths: true,
  });

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  return (
    <div className="grid gap-4">
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("budget.report.budget")} className="w-64">
          {(control) => (
            <Select
              {...control}
              value={budgetId ?? ""}
              onChange={(event) => setSearch({ budgetId: event.target.value || undefined })}
            >
              <option value="">{t("common.select")}</option>
              {(budgets.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.fiscalYear} · {item.name} (v{item.versionNo})
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("budget.report.throughMonth")} className="w-40">
          {(control) => (
            <Select
              {...control}
              value={String(throughMonth)}
              onChange={(event) => setSearch({ throughMonth: Number(event.target.value) })}
            >
              {BUDGET_MONTH_LABELS.map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {shape.isSingleStation ? null : (
          <FormField label={t("budget.fields.station")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={search.stationId ?? ""}
                onChange={(event) => setSearch({ stationId: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {(stations.data ?? []).map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        )}
        <FormField label={t("budget.report.groupBy")} className="w-40">
          {(control) => (
            <Select
              {...control}
              value={groupBy}
              onChange={(event) => setSearch({ group: event.target.value as BudgetReportGroupBy })}
            >
              <option value="ACCOUNT">{t("budget.report.groupByAccount")}</option>
              <option value="STATION">{t("budget.report.groupByStation")}</option>
            </Select>
          )}
        </FormField>
      </Card>

      {budgetId === null ? (
        <EmptyState
          headingLevel={2}
          icon={<Target />}
          title={t("budget.report.noBudgetTitle")}
          description={t("budget.report.noBudgetDescription")}
        />
      ) : report.error ? (
        <Alert tone="danger">{errorMessage(report.error)}</Alert>
      ) : !report.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <ReportTables report={report.data} />
      )}
    </div>
  );
}

function ReportTables({ report }: { report: ActualVsBudget }): ReactElement {
  const { t } = useTranslation();
  const budgeted = report.rows.filter((row) => !row.unbudgeted);
  const unbudgeted = report.rows.filter((row) => row.unbudgeted);

  return (
    <div className="grid gap-4">
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("budget.report.totals")}</h2>
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          {(["REVENUE", "EXPENSE"] as const).map((nature) => {
            const totals = nature === "REVENUE" ? report.totals.revenue : report.totals.expense;
            return (
              <div key={nature} className="grid gap-1 rounded-md border p-3">
                <dt className="text-xs text-muted-foreground">{BUDGET_NATURE_LABELS[nature]}</dt>
                <dd className="flex flex-wrap items-baseline gap-3">
                  <span>
                    {t("budget.report.budgetAmount")}: <Amount value={totals.budget} />
                  </span>
                  <span>
                    {t("budget.report.actual")}: <Amount value={totals.actual} />
                  </span>
                  <span className="font-semibold">
                    {t("budget.report.difference")}: <Amount value={totals.difference} colorize />
                  </span>
                  <VarianceBadge variance={totals} unbudgeted={false} />
                </dd>
              </div>
            );
          })}
        </dl>
        <p className="text-xs text-muted-foreground">
          {t("budget.report.threshold", { percent: report.thresholdPercent.replace(".", ",") })}
        </p>
      </Card>

      <VarianceTable title={t("budget.report.rows")} rows={budgeted} />
      {unbudgeted.length > 0 ? (
        <VarianceTable title={t("budget.report.unbudgetedRows")} rows={unbudgeted} />
      ) : null}
    </div>
  );
}

function VarianceTable({ title, rows }: { title: string; rows: ActualVsBudgetRow[] }): ReactElement {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const columns = [
    { id: "key", header: t("budget.report.cell"), exportValue: (row: ActualVsBudgetRow) => row.key },
    {
      id: "budget",
      header: t("budget.report.budgetAmount"),
      exportValue: (row: ActualVsBudgetRow) => row.yearToDate.budget,
    },
    {
      id: "actual",
      header: t("budget.report.actual"),
      exportValue: (row: ActualVsBudgetRow) => row.yearToDate.actual,
    },
    {
      id: "difference",
      header: t("budget.report.difference"),
      exportValue: (row: ActualVsBudgetRow) => row.yearToDate.difference,
    },
    {
      id: "percent",
      header: t("budget.report.percent"),
      exportValue: (row: ActualVsBudgetRow) => row.yearToDate.percent,
    },
  ];

  return (
    <Section
      title={title}
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            void exportRowsToExcel({
              fileName: "butce-gerceklesen",
              sheetName: title.slice(0, 31),
              columns: columns as never,
              rows,
            })
          }
        >
          <Download aria-hidden="true" />
          {t("budget.report.export")}
        </Button>
      }
    >
      <div className="overflow-x-auto rounded-lg border" tabIndex={0} role="group" aria-label={title}>
        <table className="w-full text-sm">
          <caption className="sr-only">{title}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                {t("budget.report.cell")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("budget.report.budgetAmount")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("budget.report.actual")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("budget.report.difference")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("budget.report.percent")}
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {t("common.status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const open = expanded.has(row.key);
              return (
                <tr key={row.key} className="border-t align-top">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-left"
                      aria-expanded={open}
                      onClick={() =>
                        setExpanded((current) => {
                          const next = new Set(current);
                          if (next.has(row.key)) next.delete(row.key);
                          else next.add(row.key);
                          return next;
                        })
                      }
                    >
                      {open ? (
                        <ChevronDown className="size-4" aria-hidden="true" />
                      ) : (
                        <ChevronRight className="size-4" aria-hidden="true" />
                      )}
                      <span>
                        <span className="font-mono text-xs text-muted-foreground">{row.accountPrefix}</span>{" "}
                        {row.accountName ?? ""}
                        {row.stationName ? ` · ${row.stationName}` : ""}
                        {row.businessUnitType ? ` · ${t(`enums.businessUnit.${row.businessUnitType}`)}` : ""}
                      </span>
                      <StatusBadge tone={row.nature === "REVENUE" ? "info" : "neutral"}>
                        {BUDGET_NATURE_LABELS[row.nature]}
                      </StatusBadge>
                    </button>
                    {open && row.months.length > 0 ? (
                      <table className="mt-2 w-full text-xs">
                        <caption className="sr-only">
                          {t("budget.report.monthlyCaption", { cell: row.key })}
                        </caption>
                        <thead className="text-muted-foreground">
                          <tr>
                            <th scope="col" className="py-1 text-left">
                              {t("budget.report.month")}
                            </th>
                            <th scope="col" className="py-1 text-right">
                              {t("budget.report.budgetAmount")}
                            </th>
                            <th scope="col" className="py-1 text-right">
                              {t("budget.report.actual")}
                            </th>
                            <th scope="col" className="py-1 text-right">
                              {t("budget.report.difference")}
                            </th>
                            <th scope="col" className="py-1 text-right">
                              {t("budget.report.percent")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Aylık sapmayı API hesaplar (yıl içi toplamla aynı saf fonksiyon). */}
                          {row.months.map((month) => (
                            <tr key={month.month} className="border-t">
                              <td className="py-1">{BUDGET_MONTH_LABELS[month.month - 1]}</td>
                              <td className="py-1 text-right">
                                <Amount value={month.budget} blankZero />
                              </td>
                              <td className="py-1 text-right">
                                <Amount value={month.actual} blankZero />
                              </td>
                              <td className="py-1 text-right">
                                <Amount value={month.difference} colorize blankZero />
                              </td>
                              <td className="py-1 text-right tabular">{percentText(month.percent)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={row.yearToDate.budget} blankZero />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={row.yearToDate.actual} blankZero />
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    <Amount value={row.yearToDate.difference} colorize />
                  </td>
                  <td className="px-3 py-2 text-right tabular">{percentText(row.yearToDate.percent)}</td>
                  <td className="px-3 py-2">
                    <VarianceBadge variance={row.yearToDate} unbudgeted={row.unbudgeted} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
